import { useState, useEffect } from "react";
import { Menu, Info } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { AawazChatInterface } from "./AawazChatInterface";
import { UniversalInput } from "@/components/UniversalInput";
import { Sidebar } from "./Sidebar";
import { useIsDarkMode } from "@/hooks/useIsDarkMode";
import { useUser } from "@/hooks/use-user";
import { cn } from "@/lib/utils";
import SidepanelIcon from "../../packages/svgs/SidepanelIcon";

export interface Message {
  id: string;
  text: string;
  timestamp: Date;
  audioUrl?: string;
  timestampsUrl?: string;
  audioResults?: Array<{ voice: string; audioUrl: string; timestampsUrl?: string }>;
  voiceSettings: {
    voice: string;
    voices?: string[];
    speed: number;
    pitch: number;
    endpoint: string;
    useSarvam?: boolean;
    sarvamLanguage?: string;
    sarvamSpeaker?: string;
    sarvamSpeakers?: string[];
    sarvamPace?: number;
  };
}

export interface VoiceConfig {
  voice: string;
  voices?: string[];
  speed: number;
  pitch: number;
  endpoint: string;
  useSarvam?: boolean;
  sarvamLanguage?: string;
  sarvamSpeaker?: string;
  sarvamSpeakers?: string[];
  sarvamPace?: number;
}

export default function TextToSpeechApp() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [voiceConfig, setVoiceConfig] = useState<VoiceConfig>({
    voice: "Sierra",
    voices: ["Sierra"],
    speed: 0,
    pitch: 1.0,
    endpoint: "speech",
    useSarvam: false,
    sarvamLanguage: "en-IN",
    sarvamSpeaker: "shubh",
    sarvamSpeakers: ["shubh"],
    sarvamPace: 1.0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const { user } = useUser();
  const isDarkMode = useIsDarkMode();
  const STORAGE_KEY = "audio_aawaz";

  // Global hotkey: Ctrl + B => Toggle sidebar visibility
  useEffect(() => {
    const onToggleSidebarHotkey = (e: KeyboardEvent) => {
      if (e.repeat) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const isTyping = tag === "input" || tag === "textarea" || (target as any)?.isContentEditable;
      if (isTyping) return;

      const isCtrlB = e.ctrlKey && (e.key === "b" || e.key === "B");
      if (!isCtrlB) return;
      e.preventDefault();
      setIsSidebarOpen((prev) => !prev);
    };

    window.addEventListener("keydown", onToggleSidebarHotkey, { capture: false });
    return () => window.removeEventListener("keydown", onToggleSidebarHotkey as any);
  }, []);

  // Load messages from sessionStorage on mount
  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setMessages(parsed.map((m: any) => ({ ...m, timestamp: new Date(m.timestamp) })));
      } catch (e) {
        console.error("Failed to load audio_aawaz from session storage", e);
      }
    }
  }, []);

  // Save messages to sessionStorage (max 3 recent), but keep all in app/page state
  useEffect(() => {
    if (messages.length > 0) {
      const lastThree = messages.slice(-3).map((m) => {
        const cleanMsg = { ...m };
        if (cleanMsg.audioUrl?.startsWith('data:')) {
          delete cleanMsg.audioUrl;
        }
        if (cleanMsg.audioResults) {
          cleanMsg.audioResults = cleanMsg.audioResults.map((r) => {
            const cleanR = { ...r };
            if (cleanR.audioUrl?.startsWith('data:')) {
              delete cleanR.audioUrl;
            }
            return cleanR;
          });
        }
        return cleanMsg;
      });
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(lastThree));
      } catch (err) {
        console.warn("Session storage save failed:", err);
      }
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [messages]);

  const handleNewChat = () => {
    setMessages([]); // Clear all messages
    sessionStorage.removeItem(STORAGE_KEY);
    // You might want to reset voice config to default as well
    setVoiceConfig({
      voice: "Sierra",
      voices: ["Sierra"],
      speed: 0,
      pitch: 1.0,
      endpoint: "speech",
      useSarvam: false,
      sarvamLanguage: "en-IN",
      sarvamSpeaker: "shubh",
      sarvamSpeakers: ["shubh"],
      sarvamPace: 1.0,
    });
  };

  const handleAuth = () => {
    // Handle authentication
  };

  const addMessage = (text: string) => {
    const displayVoice = voiceConfig.useSarvam 
      ? voiceConfig.sarvamSpeaker || 'shubh' 
      : voiceConfig.voice;

    const newMessage: Message = {
      id: Date.now().toString(),
      text,
      timestamp: new Date(),
      voiceSettings: { 
        ...voiceConfig,
        voice: displayVoice
      },
    };
    setMessages((prev) => [...prev, newMessage]);
    return newMessage;
  };

  const updateMessageAudio = (
    messageId: string,
    audioUrl: string,
    timestampsUrl?: string,
    audioResults?: Array<{ voice: string; audioUrl: string; timestampsUrl?: string }>
  ) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId ? { ...msg, audioUrl, timestampsUrl, audioResults } : msg
      )
    );
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <Sidebar
        isOpen={isSidebarOpen}
        onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        onAuth={handleAuth}
        user={user}
        onNewChat={handleNewChat}
        isDarkMode={isDarkMode}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-x-hidden">
        {/* Header */}
        <header className="border-none p-4 bg-transparent sticky top-0 z-10">
          <div className="flex items-center justify-between">
            {/* Sidebar toggle button */}
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              aria-label="Toggle sidebar"
              className={cn(
                "p-1 transition-colors duration-150 rounded-xl flex items-center justify-center",
                isDarkMode
                  ? "text-slate-300 hover:text-white hover:bg-white/10"
                  : "text-black/90 hover:text-slate-900 hover:bg-slate-200/70"
              )}
            >
              <SidepanelIcon
                className="h-6 w-6"
                isDarkMode={isDarkMode}
              />
            </button>
            <button
              onClick={() => setShowInfo(true)}
              className="p-1.5 rounded-2xl hover:bg-accent hover:text-accent-foreground transition-colors"
              aria-label="Show info"
            >
              <Info className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>

          <Dialog open={showInfo} onOpenChange={setShowInfo}>
            <DialogContent className="w-[315px] xs:w-[400px] sm:max-w-[425px] rounded-lg">
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold">Important Note</DialogTitle>
                <DialogDescription className="pt-2 text-left">
                  Please note that audio files are not automatically saved. Make sure to download any important audio files before refreshing or leaving this page, as they will not be recoverable.
                </DialogDescription>
              </DialogHeader>
            </DialogContent>
          </Dialog>
        </header>

        {/* Chat Interface */}
        <div className="flex-1 flex flex-col relative ">
          <AawazChatInterface
            messages={messages}
            isLoading={isLoading}
            onUpdateMessage={updateMessageAudio}
          />

          {/* Universal Input for all screen sizes */}
          <UniversalInput
            voiceConfig={voiceConfig}
            onVoiceConfigChange={setVoiceConfig}
            onSendMessage={addMessage}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            onUpdateMessage={updateMessageAudio}
          />
        </div>
      </div>
    </div>
  );
}
